import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { cashVoucherAPI } from '../../services';
import { CATEGORY_LABELS } from '../../utils/cashierConstants';

/**
 * #110729 — Modal tạo PHIẾU CHI LÙI CA (không trừ két). Chỉ Admin dùng.
 *
 * Người dùng chọn: CA CŨ (đã kết thúc) + KHOẢN LỆCH đã ghi nhận (dòng sổ quỹ nguồn SHIFT âm, còn
 * chưa giải trình) + số tiền ≤ phần lệch còn lại. Máy chủ kiểm lại toàn bộ ràng buộc; modal này chỉ
 * hiển thị đúng dữ liệu máy chủ trả để không gõ UUID.
 */

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'HH:mm dd/MM/yyyy');
  } catch {
    return value;
  }
};

const emptyForm = {
  shift_id: '',
  variance_ref_id: '',
  amount: '',
  category: 'OTHER',
  description: '',
  recipient_name: '',
  evidence_url: '',
};

const BackdatedVoucherModal = ({ show, branchId, branchLabel, onConfirm, onHide, loading }) => {
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    if (!branchId) return;
    setFetching(true);
    try {
      const res = await cashVoucherAPI.getBackdatedVariances(branchId);
      setData(res || { variances: [], recent_shifts: [] });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Không tải được danh sách khoản lệch',
      );
      setData({ variances: [], recent_shifts: [] });
    } finally {
      setFetching(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (show) {
      setForm(emptyForm);
      load();
    }
  }, [show, load]);

  const variances = data?.variances || [];
  const recentShifts = data?.recent_shifts || [];

  const selectedVariance = useMemo(
    () => variances.find((v) => v.id === form.variance_ref_id) || null,
    [variances, form.variance_ref_id],
  );

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleVarianceChange = (id) => {
    const v = variances.find((x) => x.id === id);
    setForm((prev) => ({
      ...prev,
      variance_ref_id: id,
      amount: v ? String(Math.round(Number(v.remaining))) : '',
    }));
  };

  const canSubmit =
    form.shift_id &&
    form.variance_ref_id &&
    Number(form.amount) > 0 &&
    form.description.trim() &&
    form.evidence_url.trim();

  const handleSubmit = () => {
    if (!canSubmit) {
      toast.error('Vui lòng chọn ca cũ, khoản lệch, nhập số tiền, mô tả và ảnh chứng từ');
      return;
    }
    if (
      selectedVariance &&
      Number(form.amount) > Number(selectedVariance.remaining) + 0.001
    ) {
      toast.error(`Số tiền vượt phần lệch còn lại (${formatCurrency(selectedVariance.remaining)})`);
      return;
    }
    onConfirm({
      branch_id: branchId,
      shift_id: form.shift_id,
      variance_ref_id: form.variance_ref_id,
      amount: Number(form.amount),
      category: form.category,
      description: form.description.trim(),
      recipient_name: form.recipient_name.trim() || undefined,
      evidence_urls: [form.evidence_url.trim()],
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Lập phiếu chi LÙI CA (không trừ két)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="small">
          Phiếu lùi ca <strong>không trừ két lần hai</strong> (số dư đã là số đếm thật của lần kiểm
          két sau). Chỉ lập được khi có khoản lệch đã ghi nhận; số tiền không vượt phần lệch còn chưa
          giải trình. Chi nhánh: <strong>{branchLabel || '—'}</strong>.
        </Alert>

        {fetching ? (
          <div className="text-center py-4">
            <Spinner size="sm" animation="border" /> Đang tải khoản lệch...
          </div>
        ) : variances.length === 0 ? (
          <Alert variant="secondary" className="mb-0">
            Không có khoản lệch nào còn chưa giải trình trong 24 giờ gần đây — chưa lập được phiếu
            chi lùi ca.
          </Alert>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Khoản lệch cần giải trình *</Form.Label>
              <Form.Select
                value={form.variance_ref_id}
                onChange={(e) => handleVarianceChange(e.target.value)}
              >
                <option value="">— Chọn khoản lệch —</option>
                {variances.map((v) => (
                  <option key={v.id} value={v.id}>
                    Thiếu {formatCurrency(v.amount)} · còn {formatCurrency(v.remaining)} · ghi lúc{' '}
                    {formatDateTime(v.recorded_at)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Ca cũ được lùi *</Form.Label>
              <Form.Select
                value={form.shift_id}
                onChange={(e) => setField('shift_id', e.target.value)}
              >
                <option value="">— Chọn ca đã kết thúc —</option>
                {recentShifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.employee?.full_name || 'Nhân viên'} · {formatDateTime(s.start_time)} –{' '}
                    {formatDateTime(s.end_time)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">
                Số tiền chi *{' '}
                {selectedVariance && (
                  <span className="text-muted fw-normal">
                    (tối đa {formatCurrency(selectedVariance.remaining)})
                  </span>
                )}
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={selectedVariance ? Math.round(Number(selectedVariance.remaining)) : undefined}
                value={form.amount}
                onChange={(e) => setField('amount', e.target.value)}
                style={{ fontSize: 16 }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Loại chi</Form.Label>
              <Form.Select
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
              >
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Mô tả *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="VD: Chi tiền mặt ca trước chưa kịp lập phiếu"
                style={{ fontSize: 16 }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Ảnh/chứng từ (URL) *</Form.Label>
              <Form.Control
                type="url"
                value={form.evidence_url}
                onChange={(e) => setField('evidence_url', e.target.value)}
                placeholder="https://..."
                style={{ fontSize: 16 }}
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label className="small fw-semibold">Người nhận tiền</Form.Label>
              <Form.Control
                value={form.recipient_name}
                onChange={(e) => setField('recipient_name', e.target.value)}
                style={{ fontSize: 16 }}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Đóng
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || fetching || variances.length === 0}
        >
          {loading ? 'Đang tạo...' : 'Tạo phiếu chi lùi ca'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BackdatedVoucherModal;
